import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Plus,
  TrashSimple,
  PencilSimple,
  Copy,
  Ticket as TicketIcon,
  CalendarBlank,
  Key,
  SignOut,
  DotsThree,
  Envelope,
  Storefront,
  UserList,
  DownloadSimple,
} from "@phosphor-icons/react";

import api from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const inputCls =
  "bg-[#0A0A0A] border-[#2A2A2A] focus-visible:ring-[#e2a9f1] focus-visible:border-[#e2a9f1] text-[#EDEDED] rounded-sm";

const emptyEvent = {
  title: "",
  description: "",
  venue: "",
  city: "",
  date: null,
  time: "20:00",
  price_couple: 20,
  price_single_lady: 10,
  capacity: 40,
  image_url: "",
  dress_code: "",
  event_type: "social",
};

export default function AdminDashboard() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [codes, setCodes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [venueInquiries, setVenueInquiries] = useState([]);
  const [waitlist, setWaitlist] = useState([]);

  const [eventForm, setEventForm] = useState(emptyEvent);
  const [editingId, setEditingId] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState({ label: "", max_uses: "", custom_code: "" });

  const fetchAll = async () => {
    try {
      const [e, c, t, s, v] = await Promise.all([
        api.get("/admin/events"),
        api.get("/admin/codes"),
        api.get("/admin/tickets"),
        api.get("/admin/contact"),
        api.get("/admin/venue-inquiries"),
      ]);
      setEvents(e.data);
      setCodes(c.data);
      setTickets(t.data);
      setSubmissions(s.data);
      setVenueInquiries(v.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // Open event dialog (new or edit)
  const openNewEvent = () => {
    setEditingId(null);
    setEventForm(emptyEvent);
    setEventDialogOpen(true);
  };

  const openEditEvent = (ev) => {
    const dt = typeof ev.date_time === "string" ? parseISO(ev.date_time) : new Date(ev.date_time);
    setEditingId(ev.id);
    setEventForm({
      title: ev.title,
      description: ev.description,
      venue: ev.venue,
      city: ev.city,
      date: dt,
      time: format(dt, "HH:mm"),
      price_couple: ev.price_couple,
      price_single_lady: ev.price_single_lady,
      capacity: ev.capacity,
      image_url: ev.image_url || "",
      dress_code: ev.dress_code || "",
      event_type: ev.event_type || "social",
    });
    setEventDialogOpen(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.venue || !eventForm.date || !eventForm.time) {
      toast.error("Please complete title, venue, date and time");
      return;
    }
    const [hh, mm] = eventForm.time.split(":");
    const dt = new Date(eventForm.date);
    dt.setHours(Number(hh), Number(mm), 0, 0);

    const payload = {
      title: eventForm.title,
      description: eventForm.description,
      venue: eventForm.venue,
      city: eventForm.city,
      date_time: dt.toISOString(),
      price_couple: Number(eventForm.price_couple),
      price_single_lady: Number(eventForm.price_single_lady),
      capacity: Number(eventForm.capacity),
      image_url: eventForm.image_url || null,
      dress_code: eventForm.dress_code || null,
      event_type: eventForm.event_type || "social",
    };

    try {
      if (editingId) {
        await api.patch(`/admin/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created");
      }
      setEventDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error("Failed to save event");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const createCode = async () => {
    try {
      const payload = {
        label: newCode.label || null,
        max_uses: newCode.max_uses ? Number(newCode.max_uses) : null,
        custom_code: newCode.custom_code || null,
      };
      const { data } = await api.post("/admin/codes", payload);
      toast.success(`Code ${data.code} created`);
      setCodeDialogOpen(false);
      setNewCode({ label: "", max_uses: "", custom_code: "" });
      fetchAll();
    } catch (err) {
      const d = err.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm("Delete this code? Existing tickets stay valid.")) return;
    try {
      await api.delete(`/admin/codes/${id}`);
      toast.success("Code removed");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]">
      {/* Top bar */}
      <header className="border-b border-[#2A2A2A] px-6 lg:px-12 py-5 flex items-center justify-between sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-40">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-2xl font-brand">Swingle Events</Link>
          <span className="w-px h-5 bg-[#2A2A2A]" />
          <span className="font-mono-overline text-[#e2a9f1]">Admin Suite</span>
        </div>
        <div className="flex items-center gap-5">
          <span className="hidden sm:block text-sm text-[#A3A3A3]">{user?.email}</span>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 font-mono-overline text-[#A3A3A3] hover:text-[#e2a9f1]"
          >
            <SignOut size={16} weight="thin" /> Sign out
          </button>
        </div>
      </header>

      <main className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
          <StatCard icon={<CalendarBlank size={20} weight="thin" />} label="Events" value={events.length} testId="stat-events" />
          <StatCard icon={<Key size={20} weight="thin" />} label="Access Codes" value={codes.length} testId="stat-codes" />
          <StatCard icon={<TicketIcon size={20} weight="thin" />} label="Tickets Sold" value={tickets.length} testId="stat-tickets" />
          <StatCard icon={<Envelope size={20} weight="thin" />} label="Contact Inbox" value={submissions.filter(s => s.status === "new").length} testId="stat-contact" />
          <StatCard icon={<Storefront size={20} weight="thin" />} label="Venue Inquiries" value={venueInquiries.filter(v => v.status === "new").length} testId="stat-venues" />
        </div>

        <Tabs defaultValue="events" className="w-full">
          <TabsList className="bg-[#111111] border border-[#2A2A2A] rounded-sm mb-8">
            <TabsTrigger
              value="events"
              data-testid="tab-events"
              className="data-[state=active]:bg-[#e2a9f1] data-[state=active]:text-[#0A0A0A] rounded-sm font-mono-overline"
            >
              Events
            </TabsTrigger>
            <TabsTrigger
              value="codes"
              data-testid="tab-codes"
              className="data-[state=active]:bg-[#e2a9f1] data-[state=active]:text-[#0A0A0A] rounded-sm font-mono-overline"
            >
              Access Codes
            </TabsTrigger>
            <TabsTrigger
              value="tickets"
              data-testid="tab-tickets"
              className="data-[state=active]:bg-[#e2a9f1] data-[state=active]:text-[#0A0A0A] rounded-sm font-mono-overline"
            >
              Tickets
            </TabsTrigger>
            <TabsTrigger
              value="contact"
              data-testid="tab-contact"
              className="data-[state=active]:bg-[#e2a9f1] data-[state=active]:text-[#0A0A0A] rounded-sm font-mono-overline"
            >
              Contact
            </TabsTrigger>
            <TabsTrigger
              value="venues"
              data-testid="tab-venues"
              className="data-[state=active]:bg-[#e2a9f1] data-[state=active]:text-[#0A0A0A] rounded-sm font-mono-overline"
            >
              Venues
            </TabsTrigger>
          </TabsList>

          {/* EVENTS TAB */}
          <TabsContent value="events">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-serif-display">All evenings</h2>
              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    onClick={openNewEvent}
                    data-testid="new-event-btn"
                    className="inline-flex items-center gap-2 bg-[#e2a9f1] text-[#0A0A0A] font-semibold px-5 py-2.5 rounded-sm hover:bg-[#efc8f7] transition-colors"
                  >
                    <Plus size={16} weight="bold" /> New Event
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#111111] border-[#2A2A2A] text-[#EDEDED] rounded-sm max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif-display text-2xl">
                      {editingId ? "Edit event" : "Create event"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <div className="sm:col-span-2">
                      <Label className="font-mono-overline text-[#A3A3A3]">Title</Label>
                      <Input
                        data-testid="event-title-input"
                        value={eventForm.title}
                        onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-mono-overline text-[#A3A3A3]">Type</Label>
                      <div className="mt-2 flex gap-3">
                        {["social", "spicy"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            data-testid={`event-type-${t}`}
                            onClick={() => setEventForm({ ...eventForm, event_type: t })}
                            className={`flex-1 py-3 rounded-sm border transition-all uppercase tracking-[0.2em] text-xs font-medium ${
                              eventForm.event_type === t
                                ? "bg-[#e2a9f1] text-[#0A0A0A] border-[#e2a9f1]"
                                : "bg-[#0A0A0A] text-[#A3A3A3] border-[#2A2A2A] hover:border-[#e2a9f1]/60"
                            }`}
                          >
                            {t === "social" ? "Social · no play" : "Spicy · play permitted"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-mono-overline text-[#A3A3A3]">Description</Label>
                      <Textarea
                        data-testid="event-description-input"
                        rows={4}
                        value={eventForm.description}
                        onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Venue</Label>
                      <Input
                        data-testid="event-venue-input"
                        value={eventForm.venue}
                        onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">City</Label>
                      <Input
                        data-testid="event-city-input"
                        value={eventForm.city}
                        onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="event-date-picker"
                            className={`mt-2 w-full justify-start ${inputCls} hover:bg-[#0A0A0A]`}
                          >
                            <CalendarBlank size={16} weight="thin" className="mr-2" />
                            {eventForm.date ? format(eventForm.date, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#111111] border-[#2A2A2A]" align="start">
                          <Calendar
                            mode="single"
                            selected={eventForm.date}
                            onSelect={(d) => setEventForm({ ...eventForm, date: d })}
                            className="bg-[#111111] text-[#EDEDED]"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Time (24h)</Label>
                      <Input
                        type="time"
                        data-testid="event-time-input"
                        value={eventForm.time}
                        onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Couple Price (£)</Label>
                      <Input
                        type="number"
                        data-testid="event-price-couple-input"
                        value={eventForm.price_couple}
                        onChange={(e) => setEventForm({ ...eventForm, price_couple: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Single Lady Price (£)</Label>
                      <Input
                        type="number"
                        data-testid="event-price-single-input"
                        value={eventForm.price_single_lady}
                        onChange={(e) => setEventForm({ ...eventForm, price_single_lady: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Capacity</Label>
                      <Input
                        type="number"
                        data-testid="event-capacity-input"
                        value={eventForm.capacity}
                        onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-mono-overline text-[#A3A3A3]">Image URL</Label>
                      <Input
                        data-testid="event-image-input"
                        value={eventForm.image_url}
                        onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                        className={inputCls + " mt-2"}
                        placeholder="https://images.unsplash.com/..."
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-mono-overline text-[#A3A3A3]">Dress Code</Label>
                      <Input
                        data-testid="event-dress-input"
                        value={eventForm.dress_code}
                        onChange={(e) => setEventForm({ ...eventForm, dress_code: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                  </div>
                  <DialogFooter className="mt-6">
                    <button
                      onClick={saveEvent}
                      data-testid="event-save-btn"
                      className="bg-[#e2a9f1] text-[#0A0A0A] font-semibold px-6 py-2.5 rounded-sm hover:bg-[#efc8f7]"
                    >
                      {editingId ? "Save changes" : "Create event"}
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-[#111111] border border-[#2A2A2A] divide-y divide-[#2A2A2A]" data-testid="events-table">
              {events.length === 0 && (
                <div className="p-8 text-center text-[#A3A3A3] font-mono-overline">No events yet</div>
              )}
              {events.map((ev) => {
                const dt = typeof ev.date_time === "string" ? parseISO(ev.date_time) : new Date(ev.date_time);
                return (
                  <div key={ev.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-event-row-${ev.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="text-lg text-[#EDEDED]">{ev.title}</div>
                        <span
                          className={`font-mono-overline text-[10px] px-2 py-0.5 ${
                            ev.event_type === "spicy"
                              ? "bg-[#e2a9f1] text-[#0A0A0A]"
                              : "bg-[#2A2A2A] text-[#A3A3A3]"
                          }`}
                          data-testid={`admin-event-tag-${ev.id}`}
                        >
                          {ev.event_type === "spicy" ? "Spicy" : "Social"}
                        </span>
                      </div>
                      <div className="text-sm text-[#A3A3A3] mt-1">
                        {format(dt, "EEE d MMM yyyy · h:mm a")} · {ev.venue}, {ev.city}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#e2a9f1] font-serif-display text-lg leading-tight">£{Number(ev.price_couple).toFixed(0)}</div>
                      <div className="font-mono-overline text-[#A3A3A3] text-[10px]">couple</div>
                      <div className="text-[#e2a9f1] font-serif-display text-base leading-tight mt-1">£{Number(ev.price_single_lady).toFixed(0)}</div>
                      <div className="font-mono-overline text-[#A3A3A3] text-[10px]">single lady</div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`event-actions-${ev.id}`}
                          className="p-2 hover:bg-[#1A1A1A] rounded-sm"
                        >
                          <DotsThree size={20} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#111111] border-[#2A2A2A] text-[#EDEDED] rounded-sm">
                        <DropdownMenuItem onClick={() => openEditEvent(ev)} data-testid={`event-edit-${ev.id}`}>
                          <PencilSimple size={14} weight="thin" className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deleteEvent(ev.id)} data-testid={`event-delete-${ev.id}`} className="text-red-400">
                          <TrashSimple size={14} weight="thin" className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* CODES TAB */}
          <TabsContent value="codes">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-serif-display">Access codes</h2>
              <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    data-testid="new-code-btn"
                    className="inline-flex items-center gap-2 bg-[#e2a9f1] text-[#0A0A0A] font-semibold px-5 py-2.5 rounded-sm hover:bg-[#efc8f7]"
                  >
                    <Plus size={16} weight="bold" /> New Code
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#111111] border-[#2A2A2A] text-[#EDEDED] rounded-sm">
                  <DialogHeader>
                    <DialogTitle className="font-serif-display text-2xl">Generate access code</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Label (optional)</Label>
                      <Input
                        data-testid="code-label-input"
                        value={newCode.label}
                        onChange={(e) => setNewCode({ ...newCode, label: e.target.value })}
                        className={inputCls + " mt-2"}
                        placeholder="e.g. Press list"
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Max uses (blank = unlimited)</Label>
                      <Input
                        data-testid="code-max-uses-input"
                        type="number"
                        min={1}
                        value={newCode.max_uses}
                        onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                        className={inputCls + " mt-2"}
                      />
                    </div>
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Custom code (optional)</Label>
                      <Input
                        data-testid="code-custom-input"
                        value={newCode.custom_code}
                        onChange={(e) => setNewCode({ ...newCode, custom_code: e.target.value.toUpperCase() })}
                        className={inputCls + " mt-2 uppercase"}
                        placeholder="Leave blank to auto-generate"
                      />
                    </div>
                  </div>
                  <DialogFooter className="mt-6">
                    <button
                      onClick={createCode}
                      data-testid="code-create-submit"
                      className="bg-[#e2a9f1] text-[#0A0A0A] font-semibold px-6 py-2.5 rounded-sm hover:bg-[#efc8f7]"
                    >
                      Generate
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-[#111111] border border-[#2A2A2A] divide-y divide-[#2A2A2A]" data-testid="codes-table">
              {codes.length === 0 && (
                <div className="p-8 text-center text-[#A3A3A3] font-mono-overline">No codes yet</div>
              )}
              {codes.map((c) => (
                <div key={c.id} className="p-5 flex items-center gap-4" data-testid={`admin-code-row-${c.id}`}>
                  <div className="flex-1">
                    <div className="text-xl tracking-[0.2em] font-mono-overline text-[#e2a9f1]">{c.code}</div>
                    {c.label && <div className="text-sm text-[#A3A3A3] mt-1">{c.label}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-mono-overline text-[#A3A3A3]">Uses</div>
                    <div className="text-[#EDEDED]">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : ""}</div>
                  </div>
                  <button
                    onClick={() => copyCode(c.code)}
                    data-testid={`code-copy-${c.id}`}
                    className="p-2 hover:bg-[#1A1A1A] rounded-sm"
                  >
                    <Copy size={18} weight="thin" />
                  </button>
                  <button
                    onClick={() => deleteCode(c.id)}
                    data-testid={`code-delete-${c.id}`}
                    className="p-2 hover:bg-[#1A1A1A] rounded-sm text-red-400"
                  >
                    <TrashSimple size={18} weight="thin" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TICKETS TAB */}
          <TabsContent value="tickets">
            <h2 className="text-3xl font-serif-display mb-6">Tickets sold</h2>
            <div className="bg-[#111111] border border-[#2A2A2A] divide-y divide-[#2A2A2A]" data-testid="tickets-table">
              {tickets.length === 0 && (
                <div className="p-8 text-center text-[#A3A3A3] font-mono-overline">No tickets sold yet</div>
              )}
              {tickets.map((t) => {
                const ev = events.find((e) => e.id === t.event_id);
                return (
                  <div key={t.id} className="p-5 grid md:grid-cols-5 gap-4 items-center" data-testid={`admin-ticket-row-${t.id}`}>
                    <div className="md:col-span-2">
                      <div className="text-[#EDEDED]">{t.full_name}</div>
                      <div className="text-sm text-[#A3A3A3]">{t.email}</div>
                    </div>
                    <div className="text-sm text-[#A3A3A3]">
                      {ev ? ev.title : "—"}
                    </div>
                    <div className="text-sm">
                      <span className="font-mono-overline text-[#e2a9f1]">{t.code_used}</span>
                      <span className="text-[#A3A3A3]"> · ×{t.quantity}</span>
                    </div>
                    <div className="text-[#e2a9f1] font-serif-display text-lg md:text-right">
                      £{Number(t.amount_paid).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* CONTACT TAB */}
          <TabsContent value="contact">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-serif-display">Contact submissions</h2>
              <span className="font-mono-overline text-[#A3A3A3]">
                {submissions.filter((s) => s.status === "new").length} new
              </span>
            </div>
            <div className="space-y-4" data-testid="contact-submissions-table">
              {submissions.length === 0 && (
                <div className="bg-[#111111] border border-[#2A2A2A] p-8 text-center text-[#A3A3A3] font-mono-overline">
                  No submissions yet
                </div>
              )}
              {submissions.map((s) => (
                <div
                  key={s.id}
                  className="bg-[#111111] border border-[#2A2A2A] p-6"
                  data-testid={`admin-contact-row-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="text-lg text-[#EDEDED]">{s.name}</div>
                      <div className="text-sm text-[#A3A3A3]">{s.email}</div>
                    </div>
                    <span
                      className={`font-mono-overline text-[10px] px-2 py-0.5 ${
                        s.status === "new"
                          ? "bg-[#e2a9f1] text-[#0A0A0A]"
                          : "bg-[#2A2A2A] text-[#A3A3A3]"
                      }`}
                    >
                      {s.status}
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 text-sm mb-4">
                    {s.telegram_username && (
                      <div>
                        <div className="font-mono-overline text-[10px] text-[#A3A3A3]">Telegram</div>
                        <div className="text-[#EDEDED]">{s.telegram_username}</div>
                      </div>
                    )}
                    {s.fab_username && (
                      <div>
                        <div className="font-mono-overline text-[10px] text-[#A3A3A3]">Fab</div>
                        <div className="text-[#EDEDED]">{s.fab_username}</div>
                      </div>
                    )}
                    {s.swinghub_username && (
                      <div>
                        <div className="font-mono-overline text-[10px] text-[#A3A3A3]">SwingHub</div>
                        <div className="text-[#EDEDED]">{s.swinghub_username}</div>
                      </div>
                    )}
                  </div>
                  <div className="text-[#EDEDED]/85 leading-relaxed whitespace-pre-wrap border-l-2 border-[#e2a9f1]/40 pl-4 mb-4">
                    {s.message}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono-overline text-[10px] text-[#A3A3A3]">
                      {new Date(s.created_at).toLocaleString()}
                    </span>
                    <div className="flex gap-2">
                      {["new", "read", "replied", "archived"]
                        .filter((st) => st !== s.status)
                        .map((st) => (
                          <button
                            key={st}
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/contact/${s.id}?status=${st}`);
                                fetchAll();
                              } catch {
                                toast.error("Could not update");
                              }
                            }}
                            data-testid={`contact-mark-${st}-${s.id}`}
                            className="font-mono-overline text-[10px] text-[#A3A3A3] hover:text-[#e2a9f1] px-2 py-1 border border-[#2A2A2A] hover:border-[#e2a9f1]/60 transition-colors"
                          >
                            Mark {st}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* VENUES TAB */}
          <TabsContent value="venues">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-serif-display">Venue inquiries</h2>
              <span className="font-mono-overline text-[#A3A3A3]">
                {venueInquiries.filter((v) => v.status === "new").length} new
              </span>
            </div>
            <div className="space-y-4" data-testid="venue-inquiries-table">
              {venueInquiries.length === 0 && (
                <div className="bg-[#111111] border border-[#2A2A2A] p-8 text-center text-[#A3A3A3] font-mono-overline">
                  No venue inquiries yet
                </div>
              )}
              {venueInquiries.map((v) => (
                <div
                  key={v.id}
                  className="bg-[#111111] border border-[#2A2A2A] p-6"
                  data-testid={`admin-venue-row-${v.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="text-lg text-[#EDEDED]">{v.venue_name}</div>
                      <div className="text-sm text-[#A3A3A3]">
                        {v.contact_name} · {v.email} · {v.contact_number}
                      </div>
                      {v.venue_website && (
                        <a
                          href={v.venue_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#e2a9f1] hover:underline mt-1 inline-block"
                        >
                          {v.venue_website}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {v.venue_type && (
                        <span className="font-mono-overline text-[10px] bg-[#2A2A2A] text-[#A3A3A3] px-2 py-0.5">
                          {v.venue_type}
                        </span>
                      )}
                      <span
                        className={`font-mono-overline text-[10px] px-2 py-0.5 ${
                          v.status === "new"
                            ? "bg-[#e2a9f1] text-[#0A0A0A]"
                            : "bg-[#2A2A2A] text-[#A3A3A3]"
                        }`}
                      >
                        {v.status}
                      </span>
                    </div>
                  </div>
                  {v.message && (
                    <div className="text-[#EDEDED]/85 leading-relaxed whitespace-pre-wrap border-l-2 border-[#e2a9f1]/40 pl-4 mb-4">
                      {v.message}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-mono-overline text-[10px] text-[#A3A3A3]">
                      {new Date(v.created_at).toLocaleString()}
                    </span>
                    <div className="flex gap-2">
                      {["new", "read", "replied", "archived"]
                        .filter((st) => st !== v.status)
                        .map((st) => (
                          <button
                            key={st}
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/venue-inquiries/${v.id}?status=${st}`);
                                fetchAll();
                              } catch {
                                toast.error("Could not update");
                              }
                            }}
                            data-testid={`venue-mark-${st}-${v.id}`}
                            className="font-mono-overline text-[10px] text-[#A3A3A3] hover:text-[#e2a9f1] px-2 py-1 border border-[#2A2A2A] hover:border-[#e2a9f1]/60 transition-colors"
                          >
                            Mark {st}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const StatCard = ({ icon, label, value, testId }) => (
  <div className="bg-[#111111] border border-[#2A2A2A] p-6" data-testid={testId}>
    <div className="text-[#e2a9f1] mb-3">{icon}</div>
    <div className="font-mono-overline text-[#A3A3A3]">{label}</div>
    <div className="text-4xl font-serif-display text-[#EDEDED] mt-2">{value}</div>
  </div>
);
